// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    canSaveOrder,
    getCurrentCartState,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartSaveOrderButton from "@sc/Overrides/Widgets/Cart/CartSaveOrderButton";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        cart,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: canSaveOrder(cart),
        canAddAllToList: canAddAllToList(cart),
        isPunchOutOrder: cart?.properties?.isPunchout?.toLowerCase() === "true",
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isLoading: state.data.carts.isLoading,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    // XNG-Changes
    clearCurrentCart,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartActionsStyles {
    narrowHidden?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: ClickablePresentationProps;
    addAllToListClickable?: ClickablePresentationProps;
    requestAQuoteClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    saveOrderLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
    removeAndAddAllToListWrapper?: InjectableCss;
    removeAllConfirmationModalStyles?: TwoButtonModalStyles;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        above: "sm",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    wideHidden: {
        below: "md",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    wideOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 20px;
            `,
        },
    },
};

const styles = cartActionsStyles;

const CartActions: FC<Props> = ({
    cart,
    isCartEmpty,
    canAddAllToList,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    isPunchOutOrder,
    clearCurrentCart,
    isRemovingCartLine,
    isLoading,
}) => {
    // <XNG-Changes>
    const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
    const toggleRemoveAllModal = () => {
        setRemoveModalOpen(!isRemoveModalOpen);
    };

    const toasterContext = React.useContext(ToasterContext);

    // </XNG-Changes>
    if (!cart || isRemovingCartLine || isLoading[cart.id]) {
        return null;
    }

    const overflowItems = [];
    const wideHiddenOverflowMenu = [];

    const addAllToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    // if (!isCartEmpty) {
    //     overflowItems.push(
    //         <Clickable
    //             {...styles.addAllToListClickable}
    //             key="addAll"
    //             disabled={!canAddAllToList}
    //             onClick={addAllToListClickHandler}
    //             data-test-selector="cartActionsAddAllToList"
    //         >
    //             {translate("Add All to List")}
    //         </Clickable>,
    //     );

    //     wideHiddenOverflowMenu.push(
    //         <OverflowMenu key="overflow" {...styles.wideOverflowMenu} data-test-selector="cartActionsOverflowMenu">
    //             {overflowItems}
    //         </OverflowMenu>,
    //     );
    // }
    // XNG-CHANGES : Update strucutre and add ( Add All to List , Remove All) Buttons.
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                {!isPunchOutOrder && <CartSaveOrderButton variant="link" extendedStyles={styles.saveOrderLink} />}
                <StyledWrapper {...styles.removeAndAddAllToListWrapper}>
                    <Link
                        {...styles.addAllToListClickable}
                        data-test-selector="cartActionsAddAllToList"
                        onClick={addAllToListClickHandler}
                        disabled={!canAddAllToList}
                        aria-label="Add All to List"
                    >
                        {translate("Add All to List")}
                    </Link>
                    <Link
                        onClick={toggleRemoveAllModal}
                        {...styles.removeAllLink}
                        data-test-selector="cartlineHeader_removeAll"
                        aria-label="Remove All"
                        disabled={!cart.cartLines?.length}
                    >
                        {translate("Remove All")}
                    </Link>
                </StyledWrapper>
                {/* <Hidden {...styles.narrowHidden} data-test-selector="cartActionsNarrow">
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <CartSaveOrderButton variant="clickable" extendedStyles={styles.saveOrderClickable} />
                    {overflowItems}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.wideHidden} data-test-selector="cartActionsWide">
                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
                {wideHiddenOverflowMenu}
            </Hidden> */}
            </StyledWrapper>
            <TwoButtonModal
                headlineText="Clear Cart"
                modalIsOpen={isRemoveModalOpen}
                messageText={siteMessage("Custom_SC_Cart_RemoveAllConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={toggleRemoveAllModal}
                onSubmit={() => {
                    clearCurrentCart();
                    toggleRemoveAllModal();
                }}
                submitTestSelector="RemoveAllCartLinesSubmit"
                dataTestSelector="twoButtonModal_RemoveAllCartLines"
                extendedStyles={styles.removeAllConfirmationModalStyles}
            ></TwoButtonModal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartActions),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
